// План событий и пороги гейта. SMY-82, SMY-84 · P8 Э8.1
//
// Главное решение файла: СОБЫТИЙ ЗАПИСЫВАЕТСЯ ЧЕТЫРЕ, а не двенадцать.
//
// SMY-82 перечисляет двенадцать фактов, которые нужны воронке. Восемь из них
// уже лежат в базе состоянием: регистрация — это `users.created_at`, запуск и
// его исход — `runs`, оплата — `payments`, отмена подписки —
// `subscriptions.cancelled_at`, бесплатная правка — `artifacts.edit_uses`.
// Записать их вторично в журнал событий значило бы завести второй источник
// истины ровно там, где продукт от него уже отказался: таблицы `balances` нет
// именно поэтому (см. шапку `ledger` в схеме).
//
// Разойтись эти два источника могут только в одну сторону — журнал событий
// теряет запись при сбое, а состояние остаётся, — и тогда воронка покажет
// меньше оплат, чем их было. Считать деньги по журналу отправленных событий
// нельзя.
//
// Поэтому здесь два списка, и оба явные. `LOGGED_EVENTS` — то, чего в
// состоянии нет и быть не может. `DERIVED_FACTS` — то, что считается из
// состояния, с указанием, откуда именно. Второй список нужен не для красоты:
// без него через полгода кто-нибудь допишет пятое событие «оплатил» — и оно
// будет расходиться с `payments` молча.
//
// ПРИВАТНОСТЬ. В событии нет ни текста запроса, ни номера телефона, ни имени.
// Всё, что можно записать, перечислено типами ниже, и каждое поле —
// перечисление, а не строка. Это то же правило, что у телеметрии запусков
// (docs/data-flow.md), и держится оно так же: структурой, а не обещанием.

import type { ScenarioId } from './config.ts'
import type { TariffId } from './tariffs.ts'
import type { UnitId } from './units.ts'

// --- Что записывается журналом ----------------------------------------------

/**
 * События, которых нет в состоянии.
 *
 * Каждое попало сюда потому, что не оставляет следа: экран 04, с которого
 * ушли, не создаёт запуска; упёршийся в лимит не создаёт вообще ничего;
 * обращения в поддержку пока нет как сущности.
 */
export const LOGGED_EVENTS = [
  /** Экран 04 показан. Пишет сервер: шит собирает он. */
  'confirm_shown',
  /**
   * Что человек на экране 04 выбрал. Пишет КЛИЕНТ, и это единственное
   * событие, которому мы верим на слово.
   *
   * Иначе никак: «подтвердил» ещё виден запуском, а «отменил» — это уход с
   * экрана, и сервер о нём не узнаёт ничем. Вывести отмену как «шит показали,
   * запуска не было» нельзя: так же выглядит человек, который отвлёкся на
   * пять минут и вернулся.
   */
  'confirm_decided',
  /**
   * Лимит кончился: запуск не начался, потому что остаток нулевой.
   *
   * Ни строки в `runs`, ни записи в журнале списаний это не создаёт — по
   * определению: списывать не за что. Между тем это ровно тот момент, из
   * которого растёт покупка пакета, и без него воронка обрывается.
   */
  'limit_reached',
  /**
   * Обращение в поддержку с темой. Канал появится в SMY-86 (Э8.2).
   *
   * Событие заведено заранее осознанно: гейт спрашивает «доля тикетов про
   * непонимание лимитов < 10%», и метрика, у которой нет источника, обязана
   * честно отвечать «нет данных», а не отсутствовать. Пустой счётчик виден,
   * ненаписанный — нет.
   */
  'support_asked',
  /**
   * Предложение зарегистрироваться показано. SMY-180
   *
   * Следа не оставляет по определению: показ — не действие человека, и в
   * состоянии его нет. Без него мы знали бы, сколько людей отказалось
   * (`signup_asked_count`), но не сколько увидело.
   */
  'signup_prompt_shown',
  /**
   * Что человек выбрал на предложении: пошёл регистрироваться или пропустил.
   *
   * Пишет КЛИЕНТ, и по той же причине, что `confirm_decided`: «пропустил» —
   * это закрытие окна, и сервер о нём узнаёт только потому, что клиент
   * сказал. Счётчик отказов на сервере говорит, СКОЛЬКО раз отказались, но не
   * когда и не после какого чата.
   */
  'signup_prompt_decided',
  /**
   * Упёрся в стену: запуск не начался, потому что нужна регистрация.
   *
   * Сосед `limit_reached` и по смыслу («запуск не начался, строки в `runs`
   * нет»), и по ценности: это точка, из которой растёт регистрация, и без
   * неё воронка обрывается ровно там, где интереснее всего.
   */
  'signup_wall_hit',
] as const

export type LoggedEvent = (typeof LOGGED_EVENTS)[number]

/**
 * Ступень лестницы регистрации. SMY-180
 *
 * ⚠️ ПЕРЕЧИСЛЕНИЕМ, А НЕ ЧИСЛОМ. Числа (4, 6, 9) правятся из конфигурации —
 * это ручка, которой регулируют давление на регистрацию, и её покрутят
 * первой. Записанное в событие число сделало бы старые события несравнимыми
 * с новыми после первой же правки порогов: «показали на четвёртом» и
 * «показали на третьем» — это одна и та же первая просьба, а не две разные.
 */
export const SIGNUP_STEPS = ['first', 'second', 'wall'] as const

export type SignupStep = (typeof SIGNUP_STEPS)[number]

/**
 * Решение на предложении зарегистрироваться.
 *
 * Два значения, а не три: «ушёл, не нажав» здесь неотличимо от «пропустил» —
 * закрытие окна и есть пропуск. Отдельное `dismissed` обещало бы различие,
 * которого нет.
 */
export const SIGNUP_DECISIONS = ['accepted', 'skipped'] as const

export type SignupDecision = (typeof SIGNUP_DECISIONS)[number]

/** Решение на экране 04. */
export const CONFIRM_DECISIONS = [
  'confirmed',
  'cancelled',
  /** Поставил «больше не спрашивать» — согласие и настройка одним жестом. */
  'muted',
  /** Отказался в пользу обычного ответа: кнопка «ответить в чате». */
  'switched',
] as const

export type ConfirmDecision = (typeof CONFIRM_DECISIONS)[number]

/**
 * Что именно случилось на экране 04 — по трём фактам, которые видит клиент.
 *
 * Правило живёт в ядре, а не в вёрстке, потому что экранов будет три (веб,
 * iOS, Android), а решение одно. Разъехавшись, они дадут отчёт, в котором
 * доля отмен зависит от платформы, — и объяснить эту разницу будет нечем.
 *
 * `muted` СИЛЬНЕЕ `confirmed`, а не вместо него: галочка сохраняется только
 * при настоящем запуске (правило SMY-60), то есть человек и запустил, и
 * попросил больше не спрашивать. Поэтому число подтверждений — это
 * `confirmed + muted + switched`, и в отчётах они складываются, а не
 * выбираются. Ровно поэтому подписи в отчёте называют действие целиком
 * («запустил и просил не спрашивать»), а не одну его половину.
 */
export function confirmDecisionOf(facts: {
  /** Запуск начался — человек нажал действие, а не ушёл. */
  ran: boolean
  /** Стояла галочка «больше не спрашивать». */
  skipAsked: boolean
  /** Выбрал обычный ответ вместо предложенного — кнопка отказа. */
  declined: boolean
}): ConfirmDecision {
  if (!facts.ran) return 'cancelled'
  // Отказ в пользу обычного ответа проверяется ПЕРВЫМ: человек, нажавший
  // «ответить в чате» с галочкой, отказался от дорогой единицы — и это про
  // предложение, а не про то, спрашивать ли впредь.
  if (facts.declined) return 'switched'
  return facts.skipAsked ? 'muted' : 'confirmed'
}

/**
 * Темы обращений. Список закрытый, потому что гейт считает долю ОДНОЙ из них:
 * свободная строка сделала бы метрику несчитаемой в тот же день.
 */
export const SUPPORT_TOPICS = [
  /** Тот самый: «почему списалось», «сколько осталось», «за что деньги». */
  'limits',
  'quality',
  'payment',
  'bug',
  'other',
] as const

export type SupportTopic = (typeof SUPPORT_TOPICS)[number]

/** Одно событие журнала. Полей под содержимое нет и не появится. */
export interface ProductEvent {
  event: LoggedEvent
  userId: string
  unit?: UnitId
  scenario?: ScenarioId
  decision?: ConfirmDecision
  topic?: SupportTopic
}

// --- Что считается из состояния ---------------------------------------------

/**
 * Факты воронки, у которых источник — состояние, а не журнал.
 *
 * Список нужен, чтобы «план событий» из SMY-82 был проверяемым: тест сверяет,
 * что каждый названный в задаче факт ровно один раз попал либо сюда, либо в
 * `LOGGED_EVENTS`. Факт, оказавшийся в обоих списках, — это и есть тот самый
 * второй источник истины, ради недопущения которого файл написан.
 */
export const DERIVED_FACTS = [
  { fact: 'registration', from: 'users.created_at' },
  { fact: 'first_run', from: 'min(runs.created_at)' },
  { fact: 'first_result', from: "min(runs.finished_at) where outcome = 'done'" },
  { fact: 'run_outcome', from: 'runs.outcome, runs.scenario, runs.unit' },
  { fact: 'free_edit_used', from: 'artifacts.edit_uses > 0' },
  { fact: 'pack_bought', from: "packs.source = 'purchase'" },
  { fact: 'subscribed', from: "payments.status = 'paid'" },
  { fact: 'subscription_cancelled', from: 'subscriptions.cancelled_at' },
] as const

// --- Активация ---------------------------------------------------------------

/**
 * Полезный результат в первые пять минут — определение активации из SMY-82.
 *
 * ⚠️ ТОЧКА ОТСЧЁТА СМЕНИЛАСЬ 09.09.2026 (SMY-179): пять минут считаются от
 * ПЕРВОГО ЗАПУСКА ПРИЛОЖЕНИЯ, а раньше считались от регистрации.
 *
 * Обоснование окна при этом не изменилось ни на слово и остаётся тем же:
 * обещание продукта — «пришёл и получил», вместе с выбором сценария и набором
 * текста, а не скорость нашего движка. Изменилось другое — момент, когда
 * человек у нас появляется. С гостевым режимом он появляется установкой, и
 * считать пять минут от регистрации значило бы мерить время после того, как
 * он уже четырежды получил результат.
 */
export const ACTIVATION_WINDOW_MINUTES = 5

/**
 * «Полезный» = завершённый успехом.
 *
 * Отменённый, упавший и заблокированный не годятся по одной причине: за них
 * не списывается единица (P5 Э5.1), то есть мы сами уже признали их
 * бесполезными. Метрика, считающая иначе, противоречила бы биллингу.
 */
export const ACTIVATION_OUTCOME = 'done'

// --- Сегменты beta -----------------------------------------------------------

/**
 * Разрез «сегмент» из SMY-82. Аудитория беты названа в описании P8.
 *
 * Ставится админкой при рекрутинге (SMY-85), а не спрашивается у человека:
 * лишний экран на входе стоит дороже, чем разрез в отчёте, а рекрутинг и так
 * идёт по каналам, каждый из которых и есть сегмент.
 *
 * Пусто — «пришёл сам». Это не пропуск в данных, а отдельный ответ, и в
 * отчётах он показывается своей строкой.
 */
export const USER_SEGMENTS = [
  'freelance',
  'smm',
  'marketplace',
  'smb',
  'student',
  'other',
] as const

export type UserSegment = (typeof USER_SEGMENTS)[number]

export const SEGMENT_LABELS: Record<UserSegment, string> = {
  freelance: 'Фрилансеры и самозанятые',
  smm: 'SMM и контент',
  marketplace: 'Продавцы маркетплейсов',
  smb: 'Малый бизнес',
  student: 'Студенты',
  other: 'Другое',
}

/**
 * Разреза «вариант позиционирования из P1» НЕ БУДЕТ, и это не забытая
 * половина задачи.
 *
 * P1 отменён (проект в статусе Canceled), трафик на три лендинга не
 * закупался, вариантов позиционирования не существует физически. Колонка под
 * них была бы всегда пустой, а строка в отчёте — всегда нулевой; вместо этого
 * разрез назван отсутствующим здесь, один раз.
 */
export const POSITIONING_VARIANTS = [] as const

// --- Пороги гейта. SMY-84 ----------------------------------------------------

/** Куда должно смотреть значение, чтобы порог считался пройденным. */
export type Direction = 'min' | 'max'

export interface GateMetric {
  id: string
  label: string
  /** Порог из таблицы в описании P8. */
  threshold: number
  direction: Direction
  /** Доля — печатается процентом; рубли и штуки — числом. */
  kind: 'share' | 'rub' | 'count'
  /**
   * Сколько наблюдений нужно, чтобы значение вообще что-то значило.
   *
   * Не украшение: на выборке из трёх человек любая доля равна 0, 33, 67 или
   * 100 процентам, и «не проходим» на такой выборке — это шум, принятый за
   * решение. Метрика с недобором отвечает «нет данных» и в вердикт не идёт.
   */
  minSample: number
  /** Чем метрика меряется — печатается рядом с выборкой. */
  sampleOf: string
  /**
   * Источника ещё нет, и вот когда он будет.
   *
   * Метрика гейта, которую пока нечем посчитать, обязана стоять в списке и
   * называть причину поимённо. Убрать её из списка «пока не считается» —
   * значит тихо сократить гейт до того, что удалось измерить; сказать про
   * неё «нечего считать» без объяснения — значит оставить читателя гадать,
   * данных мало или работа не сделана.
   */
  awaiting?: string
}

/**
 * Таблица гейта из описания P8, слово в слово.
 *
 * Пороги живут кодом, а не в конфигурации, намеренно: гейт — это обещание,
 * данное себе до замера. Порог, который можно подвинуть из админки, будет
 * подвинут в тот день, когда метрика в него не уложится.
 */
export const GATE_METRICS: readonly GateMetric[] = [
  {
    id: 'activation',
    label: 'Активация: результат в первые 5 минут',
    // В таблице P8 у активации написано «измеряем, целевой рост», числа нет.
    // Ноль — честная запись этого: любое значение выше нуля формально
    // проходит, а смотреть надо на движение от недели к неделе.
    threshold: 0,
    direction: 'min',
    kind: 'share',
    minSample: 30,
    sampleOf: 'зарегистрировавшихся',
  },
  {
    id: 'return_next_week',
    label: 'Возврат на следующей неделе',
    threshold: 0.35,
    direction: 'min',
    kind: 'share',
    minSample: 30,
    sampleOf: 'активированных',
  },
  {
    id: 'retention_d30',
    label: 'D30 retention',
    threshold: 0.2,
    direction: 'min',
    kind: 'share',
    minSample: 30,
    sampleOf: 'доживших до 30 дней',
  },
  {
    id: 'trial_to_paid',
    label: 'Trial-to-paid (кому предлагали платить)',
    threshold: 0.07,
    direction: 'min',
    kind: 'share',
    minSample: 30,
    sampleOf: 'без выданного тарифа',
  },
  {
    id: 'paid_retention',
    label: 'Удержание платящих после первого продления',
    threshold: 0.65,
    direction: 'min',
    kind: 'share',
    minSample: 10,
    sampleOf: 'доживших до продления',
  },
  {
    id: 'two_task_types',
    label: 'Используют два разных типа задач',
    threshold: 0.3,
    direction: 'min',
    kind: 'share',
    minSample: 30,
    sampleOf: 'активных',
  },
  {
    id: 'cogs_share',
    label: 'Себестоимость как доля выручки',
    threshold: 0.3,
    direction: 'max',
    kind: 'share',
    minSample: 1,
    sampleOf: 'рублей выручки',
  },
  {
    id: 'limit_tickets',
    label: 'Тикеты про непонимание лимитов',
    threshold: 0.1,
    direction: 'max',
    kind: 'share',
    minSample: 20,
    sampleOf: 'обращений',
    awaiting: 'SMY-86 — поддержка внутри продукта',
  },
  {
    id: 'refunds',
    label: 'Возвраты и платёжные споры',
    threshold: 0.02,
    direction: 'max',
    kind: 'share',
    minSample: 20,
    sampleOf: 'платежей',
  },
  {
    id: 'auto_routing',
    label: 'Автоподбор против ручного выбора',
    // В таблице P8 написано «заметная победа автоподбора», числа нет.
    // Ноль — минимальная планка: автоподбор не хуже ручного выбора. Насколько
    // победа «заметна», решает человек, глядя на обе доли рядом; порог держит
    // ту границу, ниже которой спорить уже не о чем.
    threshold: 0,
    direction: 'min',
    kind: 'share',
    minSample: 100,
    sampleOf: 'завершённых запусков',
    // Считается как разница долей успеха: автоопределённые минус выбранные
    // руками (`autoRoutingEdge` в `intents.ts`). Источник появился 07.09.2026
    // вместе со слоем намерений (SMY-124) — до него не было записано, кто
    // выбрал сценарий, и метрика стояла здесь пустой с названной причиной.
  },
  {
    id: 'provider_share',
    label: 'Доля одного поставщика в себестоимости',
    threshold: 0.4,
    direction: 'max',
    kind: 'share',
    minSample: 1,
    sampleOf: 'рублей себестоимости',
  },
]

/** Вердикт по метрике. Третье значение здесь важнее первых двух. */
export type Verdict = 'pass' | 'fail' | 'unknown'

export interface GateResult {
  metric: GateMetric
  /** Пусто — не из чего считать. */
  value: number | null
  /** Сколько наблюдений легло в значение. */
  sample: number
  verdict: Verdict
  /** Почему «нет данных». Пусто у посчитанных. */
  reason?: string
}

/**
 * Проходим ли по одной метрике.
 *
 * Три состояния, а не два, и это несущее решение дашборда. Красный на метрике,
 * которую пока не из чего посчитать, — это не осторожность, а ложь в опасную
 * сторону: D30 на второй неделе беты покажет ноль у всех, и гейт скажет
 * «останавливаемся» ровно тогда, когда останавливаться не на чем.
 */
export function evaluateGate(
  metric: GateMetric,
  value: number | null,
  sample: number,
): GateResult {
  if (value == null) {
    return {
      metric,
      value: null,
      sample,
      verdict: 'unknown',
      // Пока источника нет — называем его. «Нечего считать» на метрике, под
      // которую просто не написана работа, читается как «данных мало» и
      // усыпляет ровно там, где надо разбудить.
      reason: metric.awaiting != null ? `нечем мерить: ${metric.awaiting}` : 'нечего считать',
    }
  }
  if (sample < metric.minSample) {
    return {
      metric,
      value,
      sample,
      verdict: 'unknown',
      reason: `выборка ${sample} < ${metric.minSample} ${metric.sampleOf}`,
    }
  }
  const passed = metric.direction === 'min' ? value >= metric.threshold : value <= metric.threshold
  return { metric, value, sample, verdict: passed ? 'pass' : 'fail' }
}

/**
 * Общий ответ гейта: продолжаем или нет.
 *
 * Непосчитанная метрика НЕ проваливает гейт, но и не даёт ему сойтись:
 * решение «продолжаем» требует, чтобы всё было измерено. Иначе достаточно
 * не собрать данных по неудобной метрике, чтобы она перестала мешать.
 */
export function gateVerdict(results: readonly GateResult[]): {
  verdict: Verdict
  failed: string[]
  unknown: string[]
} {
  const failed = results.filter((r) => r.verdict === 'fail').map((r) => r.metric.id)
  const unknown = results.filter((r) => r.verdict === 'unknown').map((r) => r.metric.id)
  if (failed.length > 0) return { verdict: 'fail', failed, unknown }
  if (unknown.length > 0) return { verdict: 'unknown', failed, unknown }
  return { verdict: 'pass', failed, unknown }
}

// --- Потолок НПД -------------------------------------------------------------
//
// Виджета «доход против 2,4 млн ₽» здесь НЕТ, и это не пропуск задачи.
// Константы потолка и порога предупреждения живут в `billing/payments.ts`
// вместе с расчётом выручки — там, где они уже были заведены под SMY-62.
// Дашборд гейта их читает оттуда (`analytics/gate.ts`), а не заводит вторую
// пару: закон один, а два числа с ним однажды разойдутся.

// --- Тяжёлые пользователи. SMY-83 -------------------------------------------

/**
 * Кого считать тяжёлым: верхний процентиль по себестоимости.
 *
 * Доля, а не рублёвый порог: рублёвый устареет с первым же пересчётом цен
 * (SMY-89), а «верхние 5%» останутся собой. Вопрос, на который метрика
 * отвечает, — «сколько расходов даёт малая часть пользователей», и он про
 * форму распределения, а не про абсолютную величину.
 */
export const HEAVY_USER_PERCENTILE = 0.05

export interface HeavyShare {
  /** Сколько пользователей попало в верхний процентиль. */
  users: number
  /** Их доля в общей себестоимости. */
  costShare: number
  /** Себестоимость на одного тяжёлого против среднего по всем. */
  ratio: number
}

/**
 * Какую часть расходов дают верхние `percentile` пользователей.
 *
 * Считается здесь, а не запросом в базе, по двум причинам: правило отбора
 * («верхние 5%, но не меньше одного») — продуктовое решение, и проверять его
 * на пограничных выборках надо тестом, а не поднятым PostgreSQL.
 */
export function heavyShare(
  costs: readonly number[],
  percentile = HEAVY_USER_PERCENTILE,
): HeavyShare | null {
  const positive = costs.filter((c) => c > 0)
  if (positive.length === 0) return null

  const total = positive.reduce((a, b) => a + b, 0)
  if (total === 0) return null

  const sorted = [...positive].sort((a, b) => b - a)
  // Не меньше одного: при десяти пользователях 5% дают ноль человек, и метрика
  // молча схлопнулась бы в «тяжёлых нет» вместо «выборка мала».
  const n = Math.max(1, Math.round(sorted.length * percentile))
  const heavy = sorted.slice(0, n)
  const heavyTotal = heavy.reduce((a, b) => a + b, 0)

  return {
    users: n,
    costShare: heavyTotal / total,
    ratio: heavyTotal / n / (total / positive.length),
  }
}

// --- Убыточность тарифа. Алерт из SMY-83 ------------------------------------

/**
 * Take rate платёжного контура: эквайринг ЮKassa 3,5% + НПД 4,0%.
 *
 * Число взято из economics/cogs.md, где оно посчитано один раз. Здесь оно
 * нужно потому, что себестоимость сравнивается с НЕТТО, а не с ценой
 * подписки: 30% от цены и 30% от того, что дошло, — разные обещания, и гейт
 * говорит про второе.
 */
export const PAYMENT_TAKE_RATE = 0.075

/** С какой доли нетто тариф считается подходящим к цене вплотную. */
export const TARIFF_ALERT_SHARE = 0.7

export interface TariffMargin {
  tariff: TariffId
  priceRub: number
  netRub: number
  costRub: number
  /** Себестоимость как доля нетто. */
  share: number
  level: 'ok' | 'warn' | 'loss'
}

/**
 * Не подошла ли фактическая себестоимость тарифа к его цене.
 *
 * Бесплатный тариф сюда не попадает: у него нетто ноль, и доля от нуля не
 * значит ничего. Его расход — это стоимость привлечения, и мерить её надо
 * рублями на пользователя, а не долей выручки, которой нет.
 */
export function tariffMargin(
  tariff: TariffId,
  priceRub: number,
  costRub: number,
): TariffMargin | null {
  if (priceRub <= 0) return null
  const netRub = priceRub * (1 - PAYMENT_TAKE_RATE)
  const share = costRub / netRub
  return {
    tariff,
    priceRub,
    netRub,
    costRub,
    share,
    level: share >= 1 ? 'loss' : share >= TARIFF_ALERT_SHARE ? 'warn' : 'ok',
  }
}

// --- Профиль использования. SMY-83 ------------------------------------------

/**
 * Насколько человек выбрал свой лимит — и в какую полосу он от этого попал.
 *
 * Полосы взяты у калькулятора P1 (`PROFILES` в economics/tariffs.mjs): там
 * прогноз считался для выработки 25%, 50% и 100%. Здесь те же границы, но по
 * факту — иначе прогноз и замер окажутся про разные группы людей, и сравнить
 * их будет нельзя.
 *
 * Полоса «перебрал» есть отдельно от «полной выработки», и она не выдумана:
 * пакеты докупки живут сверх месячного лимита и с 06.09.2026 не сгорают, так
 * что выработка выше единицы — обычное состояние, а не ошибка счёта.
 */
export const USAGE_BANDS = [
  { id: 'idle', label: 'Не пользуется', upTo: 0.05 },
  { id: 'typical', label: 'Средний', upTo: 0.25 },
  { id: 'heavy', label: 'Активный', upTo: 0.5 },
  { id: 'full', label: 'Полная выработка', upTo: 1 },
  { id: 'over', label: 'Перебрал сверх лимита', upTo: Infinity },
] as const

export type UsageBand = (typeof USAGE_BANDS)[number]['id']

/**
 * В какую полосу попадает выработка.
 *
 * Границы включающие сверху: ровно четверть лимита — это «средний», а не
 * «активный». Прогноз считал профиль `typical` при rate 0,25, и человек,
 * выбравший ровно столько, обязан лечь в ту же строку, с которой его
 * сравнивают.
 */
export function usageBandOf(share: number): UsageBand {
  for (const band of USAGE_BANDS) {
    if (share <= band.upTo) return band.id
  }
  return 'over'
}

export interface ProfileMargin {
  band: UsageBand
  label: string
  users: number
  /** Средняя выработка внутри полосы. */
  avgShare: number
  /** Средняя себестоимость на человека за окно. */
  avgCostRub: number
  /** Средняя нетто-выручка на человека: цена тарифа минус платёжный контур. */
  avgNetRub: number
  /** Прибыль на человека. Отрицательная — полоса убыточна. */
  avgProfitRub: number
}

/**
 * Какой профиль использования убыточен и на сколько — прямой ответ на
 * «готово, когда» из SMY-83.
 *
 * Считается ТОЛЬКО по платящим: у получившего тариф бесплатно нетто равно
 * нулю, и любая его выработка выглядела бы убытком. Его расход — стоимость
 * привлечения, и мерить её надо рублями на человека, а не убыточностью
 * полосы.
 *
 * Чистая функция: полосы и деньги — правило, а не запрос, и проверять его на
 * пограничной выработке надо тестом, а не поднятым PostgreSQL.
 */
export function profileMargins(
  users: readonly { share: number; costRub: number; priceRub: number; granted: boolean }[],
): ProfileMargin[] {
  const paying = users.filter((u) => !u.granted && u.priceRub > 0)
  const groups = new Map<UsageBand, typeof paying>()
  for (const u of paying) {
    const band = usageBandOf(u.share)
    const bucket = groups.get(band)
    if (bucket != null) bucket.push(u)
    else groups.set(band, [u])
  }

  const avg = (list: readonly number[]) => list.reduce((a, b) => a + b, 0) / list.length

  return USAGE_BANDS.filter((b) => groups.has(b.id)).map((b) => {
    const list = groups.get(b.id)!
    const avgCostRub = avg(list.map((u) => u.costRub))
    const avgNetRub = avg(list.map((u) => u.priceRub * (1 - PAYMENT_TAKE_RATE)))
    return {
      band: b.id,
      label: b.label,
      users: list.length,
      avgShare: avg(list.map((u) => u.share)),
      avgCostRub,
      avgNetRub,
      avgProfitRub: avgNetRub - avgCostRub,
    }
  })
}
